<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { AUTHMODELHS } from '@/utils/models'
import authService from '@/services/auth.service'
import { useAuthStore } from '@/stores/auth.store'
import { onboardingSave } from '@/services/onboarding.service'
import utilModelsService from '@/services/util.models.service'
import type { IOnboardingSave } from '@/components/organisation/interface/IOnboardingSteps'
import Button from '@/components/atoms/button/index.vue'
import { HttpStatusCode } from 'axios'
import LoginRegisterBanner from '@/components/header/auth/AuthOnboardingAdvBanner/LoginRegisterBanner.vue'
import TeacherMarketingLogo from '@/assets/images/svg/AuthOnboarding/TeacherMarketingLogo.svg'
import CircleCheckIcon from '@/assets/images/svg/AuthOnboarding/CheckCircleIconSoftDev.svg'

interface ContentItem {
  heading: string
  description: string
}
const props = defineProps({
  currentOnboardingSteps: {
    type: Function,
    required: true
  },
  prefilledValues: {
    type: Object,
    required: false
  }
})

const content = ref<ContentItem[]>([
  {
    heading: 'Manage projects',
    description: 'Create, share, and invite students to coding projects'
  },
  {
    heading: 'Embed IDEs',
    description: 'Plug-in a coding environment to LMS and course materials'
  },
  {
    heading: 'Invite students & teachers',
    description: 'Create your team on JDoodle with student groups and teachers'
  },
  {
    heading: 'Create virtual assessments',
    description: 'Test knowledge with diverse question types & automate grading'
  },
  {
    heading: 'Create Assessment with AI',
    description: 'Generate assignments in a minute with JDroid'
  }
])

const authStore = useAuthStore()

const isLoading = ref<boolean>(false)
const httpErrorTimeOut = ref<NodeJS.Timeout | number | null>(null)
const httpError = ref<string>('')

/**
 * Redirect to default IDE Page ..
 */
const redirectToIde = async () => {
  let requestObj: IOnboardingSave = {
    firstName: props.prefilledValues?.firstName || authStore.onboardingSteps.firstName,
    lastName: props.prefilledValues?.lastName || authStore.onboardingSteps.lastName,
    occupation: props.prefilledValues?.occupation || authStore.onboardingSteps.occupation,
    onboarded: true,
    step: 3
  }

  isLoading.value = true
  httpError.value = ''
  await onboardingSave(requestObj)
    .then(async (res: any) => {
      if (res?.status == HttpStatusCode.Ok) {
        utilModelsService.closeModal(AUTHMODELHS.ONBOARDING)
        await authService.initAuth()
      }
    })
    .catch(() => {
      httpError.value = 'Something went wrong please try again'
    })
    .finally(() => {
      isLoading.value = false
    })
}

onMounted(async () => {
  watch(httpError, () => {
    if (httpError.value) {
      if (httpErrorTimeOut.value) clearTimeout(httpErrorTimeOut.value)
      httpErrorTimeOut.value = setTimeout(() => {
        httpError.value = ''
      }, 8000)
    }
  })
})
</script>

<template>
  <div class="flex max-h-full min-h-screen gap-x-8 px-4 pt-14 sm:p-10">
    <div class="hidden w-full rounded-3xl bg-[#EBEFF6] md:hidden lg:block lg:min-w-[45%]">
      <LoginRegisterBanner
        text="A feature-rich coding suite powered by AI"
        highlighertext="powered by AI"
        :logo="TeacherMarketingLogo"
      />
    </div>

    <div class="relative mt-8 flex w-full flex-col justify-between lg:min-w-[50%]">
      <div class="flex flex-col">
        <h1
          class="mb-3 text-center text-xl font-semibold leading-tight sm:text-left sm:text-[32px]"
        >
          Get students excited about coding!
        </h1>
        <p class="text-center text-base font-normal leading-7 sm:text-left sm:text-lg">
          Provide a holistic coding education suite for teachers.
        </p>
        <ul class="mt-8 flex flex-col gap-y-6 sm:mt-12">
          <li class="flex flex-col gap-x-3" v-for="(item, index) in content" :key="index">
            <div class="flex flex-row gap-5">
              <img :src="CircleCheckIcon" class="h-7 w-7" />
              <p class="text-base font-semibold tracking-tight sm:text-xl">{{ item.heading }}</p>
            </div>
            <div class="ml-12">
              <p class="text-lg font-normal leading-loose">{{ item.description }}</p>
            </div>
          </li>
        </ul>
        <p :class="['mt-10', 'p-normal', 'text-center', 'error', , { hidden: !httpError }]">
          {{ httpError }}
        </p>
        <p :class="['mt-10', 'p-normal', 'text-sky-950', 'text-center', { hidden: !isLoading }]">
          Updating your details. Please wait...
        </p>
      </div>

      <div
        class="mt-8 flex w-full flex-col justify-center gap-x-6 gap-y-3.5 sm:flex-row lg:mt-0 lg:justify-end"
      >
        <Button
          @click="redirectToIde"
          color="secondary"
          variant="filled"
          size="medium"
          class="h-[48px] sm:w-[171px]"
          :class="{ hidden: isLoading }"
        >
          Continue
        </Button>
      </div>
    </div>
  </div>
</template>
